"use client"

import { cn, formatDate } from "@/lib/utils"
import { BlogPost } from "@/types/blog"
import { ArrowUpRight } from "lucide-react"
import Link from "next/link"

interface BlogCardProps {
  post: BlogPost
  className?: string
}

export function BlogCard({ post, className }: BlogCardProps) {
  return (
    <Link
      href={`/blog/?slug=${post.slug}`}
      className={cn("flex flex-col h-full w-full cursor-pointer group", className)}
    >
      <div className="relative h-full w-full flex flex-col bg-white border border-border shadow-sm rounded-xl sm:rounded-2xl p-1 transition-all duration-300 hover:border-primary hover:shadow-xl hover:-translate-y-1">
        {/* Hover gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl sm:rounded-2xl pointer-events-none" />

        {/* Card Content */}
        <div className="h-full flex flex-col p-5 sm:p-6 rounded-lg sm:rounded-xl relative overflow-hidden">
          {/* Top Row: Arrow indicator */}
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div />
            <ArrowUpRight className="w-5 h-5 text-text-muted group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
          </div>

          {/* Title */}
          <h3
            className="text-lg sm:text-xl font-bold text-text mb-2 line-clamp-2 group-hover:text-primary transition-colors"
            title={post.title}
          >
            {post.title}
          </h3>

          {/* Date */}
          <time
            dateTime={new Date(post.publishedAt).toISOString()}
            className="block text-xs sm:text-sm text-text-muted mb-3 sm:mb-4"
          >
            {formatDate(post.publishedAt)}
          </time>

          {/* Excerpt */}
          <p className="text-xs sm:text-sm text-text-secondary line-clamp-3 mb-4 sm:mb-6 flex-grow leading-relaxed">
            {post.excerpt}
          </p>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mt-auto">
            {post.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-bg-alt border border-border text-text-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  )
}
